import { VisibilityMap } from '../../../types/VisibilityTypes';
import { PillsMap } from '../../canvas-pills/canvasPillsReducer';
import {
  CanvasGroup,
  CanvasNode,
  CanvasEdge,
  CanvasElementType,
} from '../../sweep-canvas/canvas-types';
import { TransientFunnel } from '../funnelMapCanvasTypes';
import * as pillsHelper from '../pillsHelper';
import { TRANSIENT_GROUP_ID } from '../useFunnelMapElements';
import { useMemo } from 'react';
import { usePlacingPluginContext } from '../../pages/funnel-map-flow/templates/placing-plugin/PlacingPluginContext';
import { getRecordTypeIdentifier } from '../../pages/configuration-canvas/utils/getRecordTypeIdentifier';
import { SweepCanvasReactFlowEdgeDataType } from '../../sweep-canvas/edges/SweepCanvasReactFlowEdgeDataType';
import { FunnelType } from '../../../types/enums/FunnelType';
import { StageType } from '../../../types/enums/StageType';
import { useGetTopTag } from './useGetTopTag';
import type { Dictionary } from 'lodash';
import { FunnelStatus } from '../../../types/enums/FunnelStatus';
import { SfFunnelMoreButtonProps } from '../types';
import { VisibilityLayers } from '../../../types/enums/VisibilityLayers';
import { CrmOrg } from '../../../types/CrmOrgs';

const groupStatusLabelMap = {
  [FunnelStatus.DRAFT]: 'Draft',
  [FunnelStatus.PENDING_CHANGES]: 'Pending Changes',
  [FunnelStatus.DEPLOYED]: undefined, // Show nothing if deployed.
  [FunnelStatus.UNREACHABLE]: undefined, // Show nothing if unreachable.
};

export const useCalculateSfFunnelsNodes = ({
  funnelMap,
  renderZeroCountPills,
  pills,
  visibilityMap,
  transientFunnel,
  objectTypesByName,
  stagesWithSparkleIcon,
  mapForecastCategoryValueToLabel,
  crmOrgs,
  filterSfPillsByCrmOrgId,
  funnelStatusMap,
  sfFunnelMoreButton,
}: {
  funnelMap: FunnelMap;
  renderZeroCountPills?: boolean;
  pills?: Partial<PillsMap>;
  visibilityMap: VisibilityMap;
  transientFunnel?: TransientFunnel;
  mapForecastCategoryValueToLabel?: (value: string) => string;
  objectTypesByName: Dictionary<ObjectTypeName>;
  stagesWithSparkleIcon?: string[];
  crmOrgs?: CrmOrg[];
  filterSfPillsByCrmOrgId?: string;
  funnelStatusMap?: Dictionary<FunnelStatus>;
  sfFunnelMoreButton?: SfFunnelMoreButtonProps;
}) => {
  const { configuringPluginType } = usePlacingPluginContext();

  const getTopTag = useGetTopTag(visibilityMap, crmOrgs);

  return useMemo(() => {
    const groups: CanvasGroup<FunnelLeadingObject>[] = [];
    const nodes: CanvasNode[] = [];
    const edges: CanvasEdge[] = [];

    const { funnelsData = {}, funnels = {} } = funnelMap;
    const getLeadingObjectForFunnel = (funnelId: string) =>
      funnelsData[funnelId]?.funnelDetails.leadingObject;

    Object.entries(funnels).forEach(([funnelId, { position, sectionId }]) => {
      const funnelData: Funnel = funnelsData[funnelId];
      if (!funnelData) {
        return;
      }

      // Groups for the funnels
      const _plugins = funnelData.funnelDetails?.plugins || {};
      const plugins = Object.keys(_plugins) as PluginTypes[];
      if (configuringPluginType?.funnelId === funnelId) {
        plugins.push(configuringPluginType.pluginType);
      }
      const objectType = getLeadingObjectForFunnel(funnelId)?.objectName || '';
      const objectLabel = objectTypesByName[objectType]?.label ?? objectType;
      const crmOrgId = funnelData.crmOrgId;
      const topTag = getTopTag(crmOrgId);
      const shouldShowPills =
        !filterSfPillsByCrmOrgId || funnelData.crmOrgId === filterSfPillsByCrmOrgId;

      const _pills = shouldShowPills
        ? pillsHelper.funnelPills({
            funnel: funnelData,
            renderZeroCountPills,
            visibilityMap,
            pills,
            objectType,
            recordTypeId: getRecordTypeIdentifier(funnelData.recordType?.name, objectType),
          })
        : undefined;

      let status: string | undefined = undefined;
      if (visibilityMap[VisibilityLayers.FUNNEL_STATUS]) {
        const groupStatus = funnelStatusMap?.[funnelId];
        status = groupStatus ? groupStatusLabelMap[groupStatus] : undefined;
      }
      const moreButton: CanvasGroup['moreButton'] = sfFunnelMoreButton?.display
        ? {
            display: true,
            isPressed: sfFunnelMoreButton.openedFunnelId === funnelId,
          }
        : undefined;

      groups.push({
        nodeType: 'group',
        id: funnelId,
        name: funnelData.name,
        position: position || { column: 0, row: 0 },
        objectType,
        objectLabel,
        metadata: funnelData?.funnelDetails.leadingObject || {
          _leadingFieldId: '0',
          _leadingFieldLabels: [],
          fieldName: 'Status',
          objectName: 'Lead',
        },
        plugins,
        pills: _pills,
        topTag,
        status,
        moreButton,
        sectionId,
      });

      const showStep = (stage: SweepStage) => {
        if (stage.stageType === StageType.LOST) {
          return false;
        }
        if (!visibilityMap.NURTURING && stage.stageType === StageType.NURTURING) {
          return false;
        }
        return true;
      };

      // Nodes for the funnel steps
      funnelData.funnelDetails.stages.filter(showStep).forEach((stage) => {
        const isNurturing = stage.stageType === StageType.NURTURING;

        const _pills = shouldShowPills
          ? pillsHelper.stepPills({
              stage,
              renderZeroCountPills,
              visibilityMap,
              pills,
            })
          : undefined;

        const funnelDetail = funnelsData[funnelId].funnelDetails;
        const leadingObject = funnelDetail.leadingObject;

        const metadata = funnelDetail.stageMetadata?.find(
          (metadata) =>
            metadata.stageName === stage.stageName &&
            metadata.leadingFieldId === leadingObject._leadingFieldId,
        );

        const node: CanvasNode = {
          nodeType: 'node',
          id: stage._stageId,
          position: {
            column: stage._stageColumnIndex || 0,
            row: stage._branchIndex || 0,
          },
          name: stage.stageName,
          type: isNurturing ? CanvasElementType.NURTURING_BUCKET : CanvasElementType.REGULAR,
          objectType: leadingObject.objectName as ObjectTypeValues,
          parentId: funnelId,
          probability: metadata?.probability,
          forecastCategoryLabel: mapForecastCategoryValueToLabel?.(metadata?.forecastCategory),
          allowNodeSwap: !isNurturing,

          pills: _pills,
          showSparkleIcon: stagesWithSparkleIcon?.includes(stage._stageId),
        };

        nodes.push(node);

        // Edges for the funnel steps
        stage.exitCriteria.forEach((exitCriteria) => {
          edges.push({
            id: exitCriteria._exitCriteriaId,
            source: stage._stageId,
            target: exitCriteria._nextStageId,
            data: {
              label: `${exitCriteria.criteria?.length}`,
              type: SweepCanvasReactFlowEdgeDataType.CIRCLE,
            },
          });
        });

        if (stage.funnelLinks?.length) {
          stage.funnelLinks.forEach((link) => {
            if (link.stageId) {
              edges.push({
                id: `${funnelId}-${stage._stageId}-${link.stageId}`,
                source: stage._stageId,
                target: link.stageId,
                data: { type: SweepCanvasReactFlowEdgeDataType.REMOVABLE, groupConnection: true },
              });
            } else {
              const targetPrefix = link._type === FunnelType.METADATA_ELEMENT ? '' : 'label-';
              const target = `${targetPrefix}${link.funnelId}`;
              edges.push({
                id: `${funnelId}-${stage._stageId}-${target}`,
                source: stage._stageId,
                target: target,
                data: { type: SweepCanvasReactFlowEdgeDataType.REMOVABLE, groupConnection: true },
              });
            }
          });
        }
      });

      funnelData.funnelDetails.funnelLinks?.forEach((link) => {
        if (link.stageId) {
          edges.push({
            id: `${funnelId}-${link.stageId}`,
            source: `label-${funnelId}`,
            target: link.stageId,
            data: { type: SweepCanvasReactFlowEdgeDataType.REMOVABLE, groupConnection: true },
          });
        } else {
          const targetPrefix = link._type === FunnelType.METADATA_ELEMENT ? '' : 'label-';
          const target = `${targetPrefix}${link.funnelId}`;
          edges.push({
            id: `${funnelId}-${target}`,
            source: `label-${funnelId}`,
            target: target,
            data: { type: SweepCanvasReactFlowEdgeDataType.REMOVABLE, groupConnection: true },
          });
        }
      });
    });

    // Transient funnel
    if (
      transientFunnel &&
      transientFunnel.type === FunnelType.SALESFORCE &&
      transientFunnel.importType === 'create'
    ) {
      const {
        name,
        data: {
          leadingObject: { _leadingFieldId, _leadingFieldLabels, fieldName, objectName },
          crmOrgId,
        },
        position,
        sectionId,
      } = transientFunnel;
      const objectLabel = objectTypesByName[objectName]?.label ?? objectName;
      const topTag = getTopTag(crmOrgId);

      groups.push({
        nodeType: 'group',
        id: TRANSIENT_GROUP_ID,
        name,
        objectType: objectName,
        objectLabel: objectLabel,
        position: position,
        metadata: {
          _leadingFieldId,
          _leadingFieldLabels,
          fieldName,
          objectName,
        },
        plugins: [],
        topTag,
        sectionId,
      });

      if (transientFunnel.data.stage) {
        nodes.push({
          nodeType: 'node',
          id: transientFunnel.data.stage._stageId,
          position: { column: 0, row: 0 },
          name: transientFunnel.data.stage.stageName,
          type: CanvasElementType.REGULAR,
          objectType: objectName as ObjectTypeValues,
          parentId: TRANSIENT_GROUP_ID,
          showSparkleIcon: false,
        });
      }
    }

    return { nodes, edges, groups };
  }, [
    funnelMap,
    transientFunnel,
    configuringPluginType,
    objectTypesByName,
    getTopTag,
    filterSfPillsByCrmOrgId,
    renderZeroCountPills,
    visibilityMap,
    pills,
    sfFunnelMoreButton,
    funnelStatusMap,
    mapForecastCategoryValueToLabel,
    stagesWithSparkleIcon,
  ]);
};
